import { useState } from "react";

import {
  getWorkbenchApiKeys,
  useGenerateWorkbenchApiKey,
} from "@vega-api/generated/api-gateway-workbench-v1/api-gateway-workbench-v1";
import { type ApiGatewayWorkbenchGeneratedApiKeyResponseData } from "@vega-api/generated/types/apiGatewayWorkbenchGeneratedApiKeyResponseData";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";

import { deriveKeyStatus, KeyStatus } from "./deriveKeyStatus";
import { InvestorUserPicker, type PickedInvestor } from "./InvestorUserPicker";
import { RevealKeyStep } from "./RevealKeyStep";
import { DEFAULT_TTL_DAYS, isValidTtl, TtlSelect } from "./TtlSelect";

type Step = "configure" | "confirmReplace" | "reveal";

type GenerateKeyDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onGenerated: VoidFunction;
  /**
   * Pre-scopes the dialog to one user (e.g. from that user's admin page):
   * the picker is not rendered and every generated key belongs to this user.
   */
  fixedUser?: PickedInvestor;
};

export const GenerateKeyDialog = ({
  open,
  setOpen,
  onGenerated,
  fixedUser,
}: GenerateKeyDialogProps) => {
  const [step, setStep] = useState<Step>("configure");
  const [pickedUser, setPickedUser] = useState<PickedInvestor | null>(null);
  const user = fixedUser ?? pickedUser;
  const [keyName, setKeyName] = useState("");
  const keyNameTouched = useBoolean();
  const [ttlDays, setTtlDays] = useState(DEFAULT_TTL_DAYS);
  const [collisionReactivates, setCollisionReactivates] = useState(false);
  const isCheckingCollision = useBoolean();
  const [generatedKey, setGeneratedKey] =
    useState<ApiGatewayWorkbenchGeneratedApiKeyResponseData | null>(null);
  const showToast = useToast();

  const trimmedKeyName = keyName.trim();
  const keyNameError =
    trimmedKeyName.length < 1 || trimmedKeyName.length > 255
      ? m.settings_reportingApiKeys_generate_keyNameRequired()
      : undefined;

  const { mutate: generateKey, isPending } = useGenerateWorkbenchApiKey({
    mutation: {
      onSuccess: (response) => {
        setGeneratedKey(response.data ?? null);
        setStep("reveal");
        showToast({
          title: m.settings_reportingApiKeys_toast_generated(),
          variant: "success",
        });
        onGenerated();
      },
      onError: () => {
        showToast({
          title: m.settings_reportingApiKeys_toast_error(),
          variant: "destructive",
        });
      },
    },
  });

  const reset = () => {
    setStep("configure");
    setPickedUser(null);
    setKeyName("");
    keyNameTouched.setFalse();
    setTtlDays(DEFAULT_TTL_DAYS);
    setCollisionReactivates(false);
    setGeneratedKey(null);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  // The reveal step must not be dismissable via Esc/click-outside: the
  // plaintext exists only in this dialog, so the only exit is the explicit
  // acknowledgment in RevealKeyStep.
  const handleSetOpen = (value: boolean) => {
    if (!value && step === "reveal") {
      return;
    }

    if (!value) {
      close();

      return;
    }
    setOpen(value);
  };

  const submit = async () => {
    if (user === null || keyNameError !== undefined || !isValidTtl(ttlDays)) {
      keyNameTouched.setTrue();

      return;
    }

    // Overwrite guard: the backend upserts, so a colliding name would
    // silently replace (and re-activate) an existing key. Check the selected
    // user's keys right before submitting and intercept collisions.
    if (step === "configure") {
      isCheckingCollision.setTrue();

      try {
        const existing = await getWorkbenchApiKeys({
          userId: user.id,
          pageSize: 500,
        });
        const collidingKey = (existing.data ?? []).find(
          (key) => key.keyName === trimmedKeyName,
        );

        if (collidingKey !== undefined) {
          setCollisionReactivates(
            deriveKeyStatus(collidingKey) === KeyStatus.Deactivated,
          );
          setStep("confirmReplace");

          return;
        }
      } catch {
        showToast({
          title: m.settings_reportingApiKeys_toast_error(),
          variant: "destructive",
        });

        return;
      } finally {
        isCheckingCollision.setFalse();
      }
    }
    generateKey({
      userId: user.id,
      keyName: trimmedKeyName,
      params: { ttlDays },
    });
  };

  const titleByStep: Record<Step, string> = {
    configure: m.settings_reportingApiKeys_generate_title(),
    confirmReplace: m.settings_reportingApiKeys_generate_replaceTitle(),
    reveal: m.settings_reportingApiKeys_reveal_title(),
  };

  const buttonsByStep: Record<
    Step,
    Parameters<typeof ActionaryModal>[0]["ModalButtonsGroupProps"]
  > = {
    configure: {
      mainActionText: m.settings_reportingApiKeys_generate_submit(),
      mainAction: () => void submit(),
      disabled:
        user === null ||
        keyNameError !== undefined ||
        isPending ||
        isCheckingCollision.value,
      isLoading: isPending || isCheckingCollision.value,
      secondaryActionText: m.btn_cancel(),
      secondaryAction: close,
    },
    confirmReplace: {
      mainActionText: m.settings_reportingApiKeys_generate_replaceConfirm(),
      mainActionVariant: "destructive",
      mainAction: () => void submit(),
      isLoading: isPending,
      secondaryActionText: m.btn_cancel(),
      secondaryAction: () => setStep("configure"),
    },
    reveal: undefined,
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={buttonsByStep[step]}
      dataTestId="reporting-api-keys-generate-dialog"
      hideCloseButton={step === "reveal"}
      open={open}
      setOpen={handleSetOpen}
      title={titleByStep[step]}
    >
      {step === "configure" ? (
        <div className="flex flex-col gap-4 text-left">
          <p className="body-base-regular text-text/secondary">
            {m.settings_reportingApiKeys_generate_userScopeNote()}
          </p>
          {fixedUser === undefined ? (
            <InvestorUserPicker onSelect={setPickedUser} />
          ) : null}
          <div>
            <InputControl
              error={keyNameTouched.value ? keyNameError : undefined}
              label={m.settings_reportingApiKeys_generate_keyName()}
              name="reporting-api-keys-key-name"
              onBlur={keyNameTouched.setTrue}
              onChange={(event) => setKeyName(event.target.value)}
              type="text"
              value={keyName}
              withBorder
            />
            <p className="body-small-regular text-text/secondary mt-1">
              {m.settings_reportingApiKeys_generate_keyNameHelper()}
            </p>
          </div>
          <TtlSelect onChange={setTtlDays} value={ttlDays} />
        </div>
      ) : null}
      {step === "confirmReplace" ? (
        <Notice
          content={
            collisionReactivates
              ? m.settings_reportingApiKeys_generate_reactivateWarning({
                  keyName: trimmedKeyName,
                })
              : m.settings_reportingApiKeys_generate_replaceWarning({
                  keyName: trimmedKeyName,
                })
          }
          title={m.settings_reportingApiKeys_noGracePeriod()}
          type="warning"
          variant="accent"
        />
      ) : null}
      {step === "reveal" && generatedKey !== null ? (
        <RevealKeyStep generatedKey={generatedKey} onDone={close} />
      ) : null}
    </ActionaryModal>
  );
};
